import React, { useEffect, useState } from 'react';
import { ImageOff, Loader2, Download } from 'lucide-react';
import { apiClient } from '@/utils/apiClient';

/**
 * Markdown <img> renderer that can load auth-guarded backend images
 * (STORY-184). Plain <img src> cannot send the Bearer token, so backend
 * paths (dashboard screenshots) are fetched through apiClient into a blob
 * object URL. External/absolute images render as a normal <img>.
 */
export const AuthImage: React.FC<{ src?: string; alt?: string }> = ({ src, alt }) => {
  const isBackendPath = !!src && src.startsWith('/api/');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isBackendPath || !src) return;
    let revoked: string | null = null;
    let cancelled = false;
    apiClient
      .fetchAuthedObjectUrl(src)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        revoked = url;
        setObjectUrl(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src, isBackendPath]);

  if (!src) return null;

  if (!isBackendPath) {
    return <img src={src} alt={alt || ''} className="max-w-full rounded-lg my-2" />;
  }

  if (failed) {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-2 my-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
        <ImageOff className="h-4 w-4" />
        {alt || 'image'} — unavailable
      </span>
    );
  }

  if (!objectUrl) {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-2 my-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        {alt || 'image'}…
      </span>
    );
  }

  const filename = src.split('/').pop() || 'screenshot.png';
  return (
    <span className="block my-2">
      <img src={objectUrl} alt={alt || ''} className="max-w-full rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm" />
      <a
        href={objectUrl}
        download={filename}
        className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >
        <Download className="h-3 w-3" />
        {filename}
      </a>
    </span>
  );
};
